import { fromUnixTime, isValid } from 'date-fns';

import { DATE_FULL_DATE } from '../../../../../../constants/userflow';
import { formatDate } from '../../../../../../utils/date';

export const getText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const text = value.trim();
  return text || null;
};

export const getTextItems = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<string[]>((items, item) => {
    const text = getText(item);
    return text ? [...items, text] : items;
  }, []);
};

export const getEvidenceQuotes = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<string[]>((quotes, evidence) => {
    if (!evidence || typeof evidence !== 'object') {
      return quotes;
    }

    const quote = getText((evidence as { quote?: unknown }).quote);
    return quote ? [...quotes, quote] : quotes;
  }, []);
};

export const getNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getScore = (value: unknown): number | null => {
  const score = getNumber(value);

  return score !== null && Number.isInteger(score) && score >= 0 && score <= 100
    ? score
    : null;
};

export const formatScore = (
  value: unknown,
  labelValue: unknown
): string | null => {
  const score = getScore(value);
  const label = getText(labelValue);

  if (score === null) {
    return label;
  }

  return label ? `${score} · ${label}` : `${score}`;
};

export const formatConfidence = (value: unknown): string | null => {
  const confidence = getNumber(value);

  if (confidence === null || confidence < 0 || confidence > 1) {
    return null;
  }

  return `${Math.round(confidence * 100)}%`;
};

export const formatGeneratedAt = (value: unknown): string | null => {
  const generatedAt = getNumber(value);

  if (generatedAt === null || generatedAt <= 0) {
    return null;
  }

  const date = fromUnixTime(generatedAt);
  return isValid(date) ? formatDate(date, DATE_FULL_DATE) : null;
};
